// src/components/SensorMap.jsx

import React, { useState, useMemo, useEffect } from 'react';
import { Droplet, Thermometer, Activity, CheckCircle, Box, Flame, Layers } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const SensorMap = ({ data, selectedZoneId }) => {
  const [selectedZone, setSelectedZone] = useState(null);
  const [viewMode, setViewMode] = useState('moisture'); // 'moisture', 'temperature', 'ph'
  const { t, language } = useLanguage();
  const locale = language === 'tr' ? 'tr-TR' : 'en-US';

  // Verileri zone'lara göre grupla ve en son değerleri al
  const zoneData = useMemo(() => {
    const zones = {};
    
    // Her zone için en son kaydı al
    data.forEach(record => {
      const zone = record.location;
      if (!zones[zone] || new Date(record.created_at) > new Date(zones[zone].created_at)) {
        zones[zone] = {
          zone,
          device: record.device,
          moisture: parseFloat(record.soilMoisture),
          temperature: parseFloat(record.soilTempC),
          ph: parseFloat(record.soilPH),
          ec: parseFloat(record.soilEC),
          humidity: parseFloat(record.humidity),
          airTemp: parseFloat(record.airTempC),
          co2: parseFloat(record.co2),
          status: record.status,
          lastUpdate: new Date(record.created_at)
        };
      }
    });

    return zones;
  }, [data]);

  const zoneIconPlacement = useMemo(() => ({
    units: ['A1', 'A2', 'B1', 'B2', 'C1', 'C2'],
    turfBox: ['B8', 'A10'],
    heating: ['A1', 'A10', 'C1', 'C10']
  }), []);

  const getZoneIconType = (zoneId) => {
    const iconTypes = [];
    if (zoneIconPlacement.units.includes(zoneId)) iconTypes.push('units');
    if (zoneIconPlacement.turfBox.includes(zoneId)) iconTypes.push('turfBox');
    if (zoneIconPlacement.heating.includes(zoneId)) iconTypes.push('heating');
    return iconTypes;
  };

  const zoneIconMap = {
    units: {
      Icon: Layers,
      className: 'bg-slate-900 text-white'
    },
    turfBox: {
      Icon: Box,
      className: 'bg-emerald-600 text-white'
    },
    heating: {
      Icon: Flame,
      className: 'bg-orange-500 text-white'
    }
  };

  const zoneTypeLabels = {
    units: t('map.unitLabel'),
    turfBox: t('map.turfBoxLabel'),
    heating: t('map.heatingLabel')
  };

  const zoneTypeDescriptions = {
    units: t('map.unitInfo'),
    turfBox: t('map.turfboxInfo'),
    heating: t('map.heatingInfo')
  };

  const selectedZoneIconTypes = selectedZone ? getZoneIconType(selectedZone.id) : [];
  const selectedZoneTypeLabels = selectedZoneIconTypes
    .map((type) => zoneTypeLabels[type])
    .filter(Boolean);

  // 30 zone'u (3x10 grid) oluştur
  const zones = useMemo(() => {
    const zones = [];
    const rows = ['A', 'B', 'C'];
    const cols = Array.from({ length: 10 }, (_, i) => i + 1);

    rows.forEach(row => {
      cols.forEach(col => {
        const zoneId = `${row}${col}`;
        zones.push({
          id: zoneId,
          data: zoneData[zoneId] || null
        });
      });
    });

    return zones;
  }, [zoneData]);

  const getStatusClass = (zone) => {
    if (!zone.data) return 'bg-slate-100 text-slate-400 border border-slate-200/70';

    const isCritical = zone.data.moisture < 20 || zone.data.temperature > 20;
    if (isCritical) return 'bg-amber-500/90 text-white border border-amber-400/80';
    return 'bg-slate-50 text-slate-700 border border-slate-200/70';
  };

  const getStatusLabel = (zone) => {
    if (!zone.data) return 'noData';

    const isCritical = zone.data.moisture < 20 || zone.data.temperature > 20;
    return isCritical ? 'critical' : 'normal';
  };

  useEffect(() => {
    const zoneIdToSelect = selectedZoneId ?? selectedZone?.id;
    if (!zoneIdToSelect) return;
    const matchingZone = zones.find((zone) => zone.id === zoneIdToSelect);
    setSelectedZone(matchingZone ?? null);
  }, [selectedZoneId, selectedZone?.id, zones]);

  // İstatistikler
  const stats = useMemo(() => {
    const zones = Object.values(zoneData);
    if (zones.length === 0) return null;

    return {
      activeZones: zones.length,
      avgMoisture: (zones.reduce((sum, z) => sum + z.moisture, 0) / zones.length).toFixed(1),
      avgTemp: (zones.reduce((sum, z) => sum + z.temperature, 0) / zones.length).toFixed(1),
      avgPH: (zones.reduce((sum, z) => sum + z.ph, 0) / zones.length).toFixed(1),
      lowMoisture: zones.filter(z => z.moisture < 20).length,
      highTemp: zones.filter(z => z.temperature > 20).length
    };
  }, [zoneData]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-slate-50/90 rounded-2xl border border-slate-200/80 shadow-sm shadow-slate-200/60 p-6 transition-shadow duration-200">
        <h2 className="text-2xl font-bold text-slate-900 mb-4">{t('map.title')}</h2>
        
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
            <div className="bg-white/80 border border-slate-200/80 rounded-xl p-3 shadow-sm shadow-slate-200/60">
              <div className="text-xs text-slate-600 font-medium">{t('map.activeZones')}</div>
              <div className="text-xl font-bold text-slate-900">{stats.activeZones}/30</div>
            </div>
            <div className="bg-white/80 border border-slate-200/80 rounded-xl p-3 shadow-sm shadow-slate-200/60">
              <div className="text-xs text-slate-600 font-medium">{t('map.avgMoisture')}</div>
              <div className="text-xl font-bold text-slate-900">{stats.avgMoisture}%</div>
            </div>
            <div className="bg-white/80 border border-slate-200/80 rounded-xl p-3 shadow-sm shadow-slate-200/60">
              <div className="text-xs text-slate-600 font-medium">{t('map.avgTemp')}</div>
              <div className="text-xl font-bold text-slate-900">{stats.avgTemp}°C</div>
            </div>
            <div className="bg-white/80 border border-slate-200/80 rounded-xl p-3 shadow-sm shadow-slate-200/60">
              <div className="text-xs text-slate-600 font-medium">{t('map.avgPH')}</div>
              <div className="text-xl font-bold text-slate-900">{stats.avgPH}</div>
            </div>
            <div className="bg-white/80 border border-slate-200/80 rounded-xl p-3 shadow-sm shadow-slate-200/60">
              <div className="text-xs text-slate-600 font-medium">{t('map.lowMoisture')}</div>
              <div className="text-xl font-bold text-slate-900">{stats.lowMoisture}</div>
            </div>
            <div className="bg-white/80 border border-slate-200/80 rounded-xl p-3 shadow-sm shadow-slate-200/60">
              <div className="text-xs text-slate-600 font-medium">{t('map.highTemp')}</div>
              <div className="text-xl font-bold text-slate-900">{stats.highTemp}</div>
            </div>
          </div>
        )}

        {/* View Mode Selector */}
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setViewMode('moisture')}
            className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${
              viewMode === 'moisture'
                ? 'bg-slate-900 text-white'
                : 'bg-gray-100 text-slate-700 hover:bg-gray-200'
            }`}
          >
            <Droplet size={18} className={viewMode === 'moisture' ? 'text-white' : 'text-slate-600'} />
            {t('map.soilMoisture')}
          </button>
          <button
            onClick={() => setViewMode('temperature')}
            className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${
              viewMode === 'temperature'
                ? 'bg-slate-900 text-white'
                : 'bg-gray-100 text-slate-700 hover:bg-gray-200'
            }`}
          >
            <Thermometer size={18} className={viewMode === 'temperature' ? 'text-white' : 'text-slate-600'} />
            {t('map.temperature')}
          </button>
          <button
            onClick={() => setViewMode('ph')}
            className={`px-4 py-2 rounded-lg font-medium flex items-center gap-2 ${
              viewMode === 'ph'
                ? 'bg-slate-900 text-white'
                : 'bg-gray-100 text-slate-700 hover:bg-gray-200'
            }`}
          >
            <Activity size={18} className={viewMode === 'ph' ? 'text-white' : 'text-slate-600'} />
            {t('map.phLevel')}
          </button>
        </div>
      </div>

      {/* Map Grid */}
      <div className="bg-slate-50/90 rounded-2xl border border-slate-200/80 shadow-sm shadow-slate-200/60 p-6 transition-shadow duration-200">
        <div className="mb-4">
          <h3 className="text-lg font-bold text-slate-900 mb-2">{t('map.fieldView')}</h3>
          <p className="text-sm text-slate-700">
            {t('map.clickForDetails')}
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-10 gap-2 mb-6">
          {zones.map((zone) => (
            <div
              key={zone.id}
              onClick={() => setSelectedZone(zone)}
              className={`
                aspect-square rounded-xl cursor-pointer transition-all
                ${getStatusClass(zone)}
                ${selectedZone?.id === zone.id ? 'ring-4 ring-emerald-400/70 scale-105 shadow-lg shadow-emerald-200/60' : ''}
                ${zone.data ? 'hover:scale-110 hover:shadow-md hover:shadow-slate-300/60 hover:ring-2 hover:ring-emerald-200/80' : 'hover:scale-105 hover:shadow-sm hover:shadow-slate-200/70'}
              `}
            >
              <div className="h-full flex flex-col items-center justify-center p-1">
                <div className="text-xs font-bold">
                  {zone.id}
                </div>
                {getZoneIconType(zone.id).length > 0 && (
                  <div className="flex flex-wrap items-center justify-center gap-1 mt-1">
                    {getZoneIconType(zone.id).map((type) => {
                      const { Icon, className } = zoneIconMap[type];
                      return (
                        <span
                          key={`${zone.id}-${type}`}
                          className={`flex items-center justify-center rounded-full w-6 h-6 shadow-sm ${className}`}
                        >
                          <Icon size={14} />
                        </span>
                      );
                    })}
                  </div>
                )}
                {zone.data && (
                  <div className="text-[10px] font-semibold">
                    {zone.data[viewMode]?.toFixed(1)}
                    {viewMode === 'moisture' && '%'}
                    {viewMode === 'temperature' && '°C'}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="border-t border-slate-200/70 pt-4">
          <h4 className="text-sm font-bold text-slate-900 mb-2">{t('map.colorLegend')}</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-amber-500/90 rounded border border-amber-400/80"></div>
              <span>{t('map.criticalLegend')}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-slate-50 rounded border border-slate-200/80"></div>
              <span>{t('map.normal')}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Selected Zone Details */}
      {selectedZone && (
        <div className="bg-slate-50/90 rounded-2xl border border-slate-200/80 shadow-sm shadow-slate-200/60 p-6 transition-shadow duration-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-slate-900">
              {t('map.zoneDetails')} {selectedZone.id}
              {selectedZoneTypeLabels.length > 0 && ` • ${selectedZoneTypeLabels.join(', ')}`}
            </h3>
            <button
              onClick={() => setSelectedZone(null)}
              className="px-3 py-1 text-sm bg-white/80 text-slate-700 border border-slate-200/80 hover:bg-slate-100 rounded-lg transition-colors duration-200"
            >
              {t('map.close')}
            </button>
          </div>

          {selectedZone.data ? (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white/80 border border-slate-200/80 rounded-xl p-4 shadow-sm shadow-slate-200/60">
                  <div className="flex items-center gap-2 mb-2">
                    <Droplet className="text-slate-600" size={20} />
                    <div className="text-sm text-slate-700 font-medium">{t('map.soilMoisture')}</div>
                  </div>
                  <div className="text-2xl font-bold text-slate-900">
                    {selectedZone.data.moisture.toFixed(1)}%
                  </div>
                </div>

                <div className="bg-white/80 border border-slate-200/80 rounded-xl p-4 shadow-sm shadow-slate-200/60">
                  <div className="flex items-center gap-2 mb-2">
                    <Thermometer className="text-slate-600" size={20} />
                    <div className="text-sm text-slate-700 font-medium">{t('map.soilTemp')}</div>
                  </div>
                  <div className="text-2xl font-bold text-slate-900">
                    {selectedZone.data.temperature.toFixed(1)}°C
                  </div>
                </div>

                <div className="bg-white/80 border border-slate-200/80 rounded-xl p-4 shadow-sm shadow-slate-200/60">
                  <div className="flex items-center gap-2 mb-2">
                    <Activity className="text-slate-600" size={20} />
                    <div className="text-sm text-slate-700 font-medium">{t('map.phLevel')}</div>
                  </div>
                  <div className="text-2xl font-bold text-slate-900">
                    {selectedZone.data.ph.toFixed(1)}
                  </div>
                </div>

                <div className="bg-white/80 border border-slate-200/80 rounded-xl p-4 shadow-sm shadow-slate-200/60">
                  <div className="flex items-center gap-2 mb-2">
                    <Activity className="text-slate-600" size={20} />
                    <div className="text-sm text-slate-700 font-medium">EC</div>
                  </div>
                  <div className="text-2xl font-bold text-slate-900">
                    {selectedZone.data.ec.toFixed(2)} mS
                  </div>
                </div>

                <div className="bg-white/80 border border-slate-200/80 rounded-xl p-4 shadow-sm shadow-slate-200/60">
                  <div className="text-sm text-slate-700 font-medium mb-1">{t('map.airTemp')}</div>
                  <div className="text-xl font-bold text-slate-900">
                    {selectedZone.data.airTemp.toFixed(1)}°C
                  </div>
                </div>

                <div className="bg-white/80 border border-slate-200/80 rounded-xl p-4 shadow-sm shadow-slate-200/60">
                  <div className="text-sm text-slate-700 font-medium mb-1">{t('map.airHumidity')}</div>
                  <div className="text-xl font-bold text-slate-900">
                    {selectedZone.data.humidity.toFixed(1)}%
                  </div>
                </div>

                <div className="bg-white/80 border border-slate-200/80 rounded-xl p-4 shadow-sm shadow-slate-200/60">
                  <div className="text-sm text-slate-700 font-medium mb-1">CO₂</div>
                  <div className="text-xl font-bold text-slate-900">
                    {selectedZone.data.co2.toFixed(0)} ppm
                  </div>
                </div>

                <div className="bg-white/80 border border-slate-200/80 rounded-xl p-4 shadow-sm shadow-slate-200/60">
                  <div className="text-sm text-slate-700 font-medium mb-1">{t('table.device')}</div>
                  <div className="text-sm font-bold text-slate-900">
                    {selectedZone.data.device}
                  </div>
                  <div className="flex items-center gap-1 mt-1">
                    <CheckCircle className="text-slate-600" size={14} />
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded border ${getStatusClass(selectedZone)}`}>
                      {t(`map.statuses.${getStatusLabel(selectedZone)}`)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="mt-4 text-xs text-slate-600">
                {t('map.lastUpdate')}: {selectedZone.data.lastUpdate.toLocaleString(locale)}
              </div>
            </>
          ) : (
            <>
              {selectedZoneIconTypes.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {selectedZoneIconTypes.map((type) => (
                    <div
                      key={`info-${selectedZone.id}-${type}`}
                      className="bg-white/80 border border-slate-200/80 rounded-xl p-4 shadow-sm shadow-slate-200/60"
                    >
                      <div className="text-sm font-semibold text-slate-900">
                        {zoneTypeLabels[type]}
                      </div>
                      <p className="text-xs text-slate-600 mt-1">
                        {zoneTypeDescriptions[type]}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="rounded-xl border border-dashed border-slate-200/80 bg-white/70 p-6 text-sm text-slate-600">
                  {t('map.statuses.noData')}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SensorMap;
